import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BadgeCheck,
  FileUp,
  Search,
  ShieldCheck,
  Smartphone,
  Users,
  X,
} from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import ConsentDocumentCapture from "../../components/consent/ConsentDocumentCapture";
import {
  getClubConsentSummary,
  grantMediaConsent,
  revokeMediaConsent,
  selfGrantMediaConsent,
} from "../../api/socialClipApi";
import { CONSENT_PURPOSES } from "../../constants/legalPolicy";
import "./ConsentLedger.css";

function readSelfUserId() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.user_id || null;
  } catch {
    return null;
  }
}

const PURPOSES = [
  {
    key: CONSENT_PURPOSES.TRAINING_ANALYSIS,
    labelKey: "consentTrainingShort",
    fallback: "Video",
  },
  {
    key: CONSENT_PURPOSES.HEART_RATE,
    labelKey: "consentHeartRateShort",
    fallback: "Heart rate",
  },
  {
    key: CONSENT_PURPOSES.IMAGE_PUBLIC_USE,
    labelKey: "consentPublicShort",
    fallback: "Public",
  },
];

const FILTERS = [
  { id: "all", labelKey: "consentFilterAll", fallback: "All" },
  { id: "video_ok", labelKey: "consentStatVideoOk", fallback: "Video cleared" },
  { id: "missing", labelKey: "consentFilterMissing", fallback: "Missing video" },
  { id: "app", labelKey: "consentStatApp", fallback: "App (18+)" },
  { id: "parent", labelKey: "consentStatParent", fallback: "Parent / signed" },
  { id: "adults", labelKey: "consentFilterAdults", fallback: "Adults 18+" },
  { id: "minors", labelKey: "consentFilterMinors", fallback: "Under 18" },
];

const STAT_FILTERS = [
  {
    id: "video_ok",
    tone: "ok",
    labelKey: "consentStatVideoOk",
    fallback: "Video cleared",
    statKey: "video_cleared",
  },
  {
    id: "missing",
    tone: "warn",
    labelKey: "consentStatMissing",
    fallback: "Missing video",
    statKey: "missing_video",
  },
  {
    id: "app",
    tone: "app",
    labelKey: "consentStatApp",
    fallback: "App (18+)",
    statKey: "adult_app",
  },
  {
    id: "parent",
    tone: "parent",
    labelKey: "consentStatParent",
    fallback: "Parent / signed",
    statKey: "parent_signed",
  },
];

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function formatGrantedAt(iso, locale) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function sourceMeta(captureMethod, t) {
  switch (captureMethod) {
    case "SELF_DIGITAL":
      return {
        tone: "app",
        label: t("consentSourceApp", "App (18+)"),
      };
    case "GUARDIAN_DIGITAL":
      return {
        tone: "parent",
        label: t("consentSourceParentApp", "Parent in app"),
      };
    case "GUARDIAN_PAPER":
      return {
        tone: "paper",
        label: t("consentSourceSigned", "Signed form"),
      };
    case "STAFF_VERIFIED":
      return {
        tone: "staff",
        label: t("consentSourceStaff", "Club record"),
      };
    default:
      return {
        tone: "none",
        label: t("consentSourceNone", "Not granted"),
      };
  }
}

function ageLabel(isAdult, t) {
  if (isAdult === true) return t("consentAdult", "18+");
  if (isAdult === false) return t("consentMinor", "Under 18");
  return t("consentAgeUnknown", "DOB missing");
}

function ageTone(isAdult) {
  if (isAdult === true) return "adult";
  if (isAdult === false) return "minor";
  return "unknown";
}

function hasVideo(row) {
  return Boolean(row?.consents?.[CONSENT_PURPOSES.TRAINING_ANALYSIS]?.granted);
}

function videoCaptureMethod(row) {
  return row?.consents?.[CONSENT_PURPOSES.TRAINING_ANALYSIS]?.capture_method;
}

function isAppConsent(row) {
  return (
    row?.is_adult === true &&
    hasVideo(row) &&
    videoCaptureMethod(row) === "SELF_DIGITAL"
  );
}

function isParentSignedConsent(row) {
  const method = videoCaptureMethod(row);
  return (
    row?.is_adult === false &&
    hasVideo(row) &&
    (method === "GUARDIAN_DIGITAL" || method === "GUARDIAN_PAPER")
  );
}

/** Same rule as the backend: minors and public use need a signed form. */
function needsSignedForm(purpose, isAdult) {
  if (purpose === CONSENT_PURPOSES.IMAGE_PUBLIC_USE) return true;
  return isAdult !== true;
}

function isGranted(row, purpose) {
  return Boolean(row?.consents?.[purpose]?.granted);
}

/**
 * A parent form normally covers both video and heart rate, so opening the
 * form for a minor from either one pre-selects both.
 */
function defaultPurposes(row, clicked) {
  const picks = [clicked];
  const sensorPair = [CONSENT_PURPOSES.TRAINING_ANALYSIS, CONSENT_PURPOSES.HEART_RATE];
  if (row?.is_adult !== true && sensorPair.includes(clicked)) {
    picks.push(...sensorPair);
  }
  return [...new Set(picks)].filter((p) => !isGranted(row, p));
}

function grantDocumentUrls(grant) {
  if (Array.isArray(grant?.document_urls) && grant.document_urls.length) {
    return grant.document_urls;
  }
  return grant?.document_url ? [grant.document_url] : [];
}

const EMPTY_FORM = {
  purposes: [CONSENT_PURPOSES.TRAINING_ANALYSIS],
  guardian_name: "",
  document_ref: "",
  pages: [],
  verified: false,
};

function matchesConsentFilter(row, filterId) {
  switch (filterId) {
    case "video_ok":
      return hasVideo(row);
    case "missing":
      return !hasVideo(row);
    case "app":
      return isAppConsent(row);
    case "parent":
      return isParentSignedConsent(row);
    case "adults":
      return row?.is_adult === true;
    case "minors":
      return row?.is_adult === false;
    default:
      return true;
  }
}

export default function ConsentLedgerPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";
  const selfUserId = useMemo(() => readSelfUserId(), []);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getClubConsentSummary();
      setItems(data?.items || []);
      setStats(data?.stats || null);
      setError("");
    } catch (err) {
      setError(err.message || t("consentLoadFailed", "Could not load consents"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((row) => {
      if (!matchesConsentFilter(row, filter)) return false;
      if (!q) return true;
      return String(row.display_name || "")
        .toLowerCase()
        .includes(q);
    });
  }, [items, query, filter]);

  const selectFilter = (next) => {
    setFilter((cur) => (cur === next ? "all" : next));
  };

  const openRecord = (row, purpose = CONSENT_PURPOSES.TRAINING_ANALYSIS) => {
    setSelected(row);
    setForm({ ...EMPTY_FORM, purposes: defaultPurposes(row, purpose) });
    setError("");
  };

  const closeRecord = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
  };

  const togglePurpose = (purpose) => {
    setForm((f) => ({
      ...f,
      purposes: f.purposes.includes(purpose)
        ? f.purposes.filter((p) => p !== purpose)
        : [...f.purposes, purpose],
    }));
  };

  const formNeedsEvidence = Boolean(
    selected && form.purposes.some((p) => needsSignedForm(p, selected.is_adult))
  );

  const submitGrant = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      if (!form.purposes.length) {
        throw new Error(
          t("consentPickPurpose", "Select at least one use covered by this form.")
        );
      }
      if (formNeedsEvidence && !form.pages.length && !form.document_ref.trim()) {
        throw new Error(
          t(
            "consentUploadRequired",
            "Photograph or upload the signed form, or enter a document reference."
          )
        );
      }
      if (selected.is_adult !== true && !form.guardian_name.trim()) {
        throw new Error(
          t("consentGuardianRequired", "Guardian name is required for under-18 players.")
        );
      }
      if (formNeedsEvidence && !form.verified) {
        throw new Error(
          t(
            "consentValidateRequired",
            "Tick the check box to confirm you verified the signed form."
          )
        );
      }

      await grantMediaConsent(
        {
          user_id: selected.user_id,
          purposes: form.purposes,
          guardian_name: form.guardian_name.trim() || undefined,
          document_ref: form.document_ref.trim() || undefined,
          staff_verified: form.verified ? "true" : undefined,
        },
        { files: form.pages.map((p) => p.file) }
      );
      closeRecord();
      await load();
    } catch (err) {
      setError(err.message || t("consentSaveFailed", "Could not record grant"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (user, purpose) => {
    if (
      !window.confirm(
        t(
          "consentWithdrawConfirm",
          "Withdraw this consent? Filming or sensors for this purpose will stop until granted again."
        )
      )
    ) {
      return;
    }
    try {
      await revokeMediaConsent({ user_id: user.user_id, purpose });
      await load();
    } catch (err) {
      setError(err.message || t("consentSaveFailed", "Could not revoke"));
    }
  };

  const grantSelf = async (purpose) => {
    setBusy(true);
    setError("");
    try {
      await selfGrantMediaConsent({ purpose });
      await load();
    } catch (err) {
      setError(
        err.message ||
          t(
            "consentSaveFailed",
            "Could not update consent. Add your date of birth on your profile if missing."
          )
      );
    } finally {
      setBusy(false);
    }
  };

  const displayStats = stats || {
    total: items.length,
    video_cleared: items.filter(hasVideo).length,
    missing_video: items.filter((r) => !hasVideo(r)).length,
    adult_app: items.filter(isAppConsent).length,
    parent_signed: items.filter(isParentSignedConsent).length,
  };

  return (
    <AdminLayout>
      <div className="cl-board">
        <div className="cl-board__glow" aria-hidden="true" />

        <header className="cl-hero">
          <div className="cl-hero__copy">
            <p className="cl-hero__kicker">
              {t("consentLedgerKicker", "GDPR · Media & sensors")}
            </p>
            <h1 className="cl-hero__title">
              {t("consentLedgerTitle", "Player consent")}
            </h1>
            <p className="cl-hero__sub">
              {t(
                "consentLedgerHelp",
                "Adults grant video and heart-rate consent in the app. Under 18 needs a parent in the Parent Hub or a signed form you upload here. Public / social use always needs a club record."
              )}
            </p>
          </div>
          <div
            className="cl-hero__stats"
            role="group"
            aria-label={t("consentFilterAll", "All")}
          >
            {STAT_FILTERS.map((stat) => {
              const active = filter === stat.id;
              return (
                <button
                  key={stat.id}
                  type="button"
                  className={`cl-stat cl-stat--${stat.tone}${
                    active ? " is-active" : ""
                  }`}
                  aria-pressed={active}
                  title={t(
                    "consentStatFilterHint",
                    "Show players in this group"
                  )}
                  onClick={() => selectFilter(stat.id)}
                >
                  <span className="cl-stat__value">
                    {displayStats[stat.statKey] ?? 0}
                  </span>
                  <span className="cl-stat__label">
                    {t(stat.labelKey, stat.fallback)}
                  </span>
                </button>
              );
            })}
          </div>
        </header>

        <div className="cl-toolbar">
          <div className="cl-search">
            <Search className="cl-search__icon" size={16} aria-hidden />
            <input
              className="cl-search__input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("consentSearchPlaceholder", "Search players…")}
              aria-label={t("consentSearchPlaceholder", "Search players…")}
            />
          </div>
          <div className="cl-filters" role="tablist" aria-label={t("consentFilterAll")}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={`cl-filter${filter === f.id ? " is-active" : ""}`}
                onClick={() => selectFilter(f.id)}
              >
                {t(f.labelKey, f.fallback)}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <p className="cl-error" role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="cl-empty">{t("loading", "Loading…")}</p>
        ) : filtered.length === 0 ? (
          <div className="cl-empty">
            <Users size={28} aria-hidden />
            <p>
              {t(
                "consentEmpty",
                "No players match this filter. Consent appears here when adults grant it in their profile or parents sign in the Parent Hub."
              )}
            </p>
          </div>
        ) : (
          <div className="cl-list">
            {filtered.map((row) => {
              const video = row.consents?.[CONSENT_PURPOSES.TRAINING_ANALYSIS];
              const source = sourceMeta(video?.capture_method, t);
              return (
                <article
                  key={row.user_id}
                  className={`cl-card${hasVideo(row) ? "" : " cl-card--gap"}`}
                >
                  <div className="cl-card__identity">
                    <div
                      className={`cl-avatar cl-avatar--${ageTone(row.is_adult)}`}
                      aria-hidden
                    >
                      {initials(row.display_name)}
                    </div>
                    <div className="cl-card__text">
                      <h2 className="cl-card__name">{row.display_name}</h2>
                      <div className="cl-card__meta">
                        <span className={`cl-pill cl-pill--${ageTone(row.is_adult)}`}>
                          {ageLabel(row.is_adult, t)}
                        </span>
                        {hasVideo(row) ? (
                          <span className={`cl-pill cl-pill--${source.tone}`}>
                            {source.tone === "app" ? (
                              <Smartphone size={12} aria-hidden />
                            ) : source.tone === "paper" ? (
                              <FileUp size={12} aria-hidden />
                            ) : (
                              <ShieldCheck size={12} aria-hidden />
                            )}
                            {source.label}
                          </span>
                        ) : (
                          <span className="cl-pill cl-pill--gap">
                            {row.is_adult === true
                              ? t("consentWaitingApp", "Waiting for app consent")
                              : t(
                                  "consentWaitingParent",
                                  "Needs parent consent or signed form"
                                )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cl-purposes">
                    {PURPOSES.map(({ key, labelKey, fallback }) => {
                      const grant = row.consents?.[key];
                      const granted = Boolean(grant?.granted);
                      const src = sourceMeta(grant?.capture_method, t);
                      const when = formatGrantedAt(grant?.granted_at, locale);
                      return (
                        <div
                          key={key}
                          className={`cl-purpose${granted ? " is-on" : " is-off"}`}
                        >
                          <div className="cl-purpose__head">
                            <span className="cl-purpose__label">
                              {t(labelKey, fallback)}
                            </span>
                            <span
                              className={`cl-purpose__status${
                                granted ? " is-on" : ""
                              }`}
                            >
                              {granted
                                ? t("yes", "Yes")
                                : t("no", "No")}
                            </span>
                          </div>
                          {granted ? (
                            <div className="cl-purpose__detail">
                              <span>{src.label}</span>
                              {when ? <span>· {when}</span> : null}
                              {grant?.guardian_name ? (
                                <span>
                                  · {grant.guardian_name}
                                </span>
                              ) : null}
                              {grant?.verified_at ? (
                                <span
                                  className="cl-purpose__verified"
                                  title={t(
                                    "consentVerifiedTitle",
                                    "Form checked by club staff"
                                  )}
                                >
                                  <BadgeCheck size={12} aria-hidden />
                                  {t("consentVerified", "Checked")}
                                </span>
                              ) : null}
                              {grantDocumentUrls(grant).length ? (
                                grantDocumentUrls(grant).map((url, i, all) => (
                                  <a
                                    key={url}
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="cl-purpose__doc"
                                  >
                                    {all.length > 1
                                      ? t("consentPageN", "Page {{n}}", { n: i + 1 })
                                      : t("consentViewForm", "View form")}
                                  </a>
                                ))
                              ) : grant?.document_ref &&
                                !String(grant.document_ref).startsWith(
                                  "guardian-digital:"
                                ) ? (
                                <span className="cl-purpose__ref">
                                  {grant.document_ref}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="cl-purpose__actions">
                            {granted ? (
                              <button
                                type="button"
                                className="cl-link"
                                onClick={() => revoke(row, key)}
                              >
                                {t("consentWithdraw", "Withdraw")}
                              </button>
                            ) : row.is_adult === true &&
                              key !== CONSENT_PURPOSES.IMAGE_PUBLIC_USE &&
                              selfUserId &&
                              String(row.user_id) === String(selfUserId) ? (
                              <button
                                type="button"
                                className="cl-link cl-link--strong"
                                disabled={busy}
                                onClick={() => grantSelf(key)}
                              >
                                {t("consentGrant", "I consent")}
                              </button>
                            ) : row.is_adult === true &&
                              key !== CONSENT_PURPOSES.IMAGE_PUBLIC_USE ? (
                              <span className="cl-hint">
                                {t(
                                  "consentAdultHint",
                                  "Player grants this in Profile"
                                )}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="cl-link cl-link--strong"
                                onClick={() => openRecord(row, key)}
                              >
                                {row.is_adult === false
                                  ? t("consentUploadForm", "Upload signed form")
                                  : t("consentRecord", "Record form")}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {selected ? (
          <div
            className="cl-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cl-modal-title"
          >
            <button
              type="button"
              className="cl-modal__backdrop"
              aria-label={t("cancel", "Cancel")}
              onClick={closeRecord}
            />
            <form className="cl-modal__panel" onSubmit={submitGrant}>
              <div className="cl-modal__head">
                <div>
                  <p className="cl-modal__kicker">
                    {selected.is_adult === false
                      ? t("consentUploadForm", "Upload signed form")
                      : t("consentRecord", "Record form")}
                  </p>
                  <h2 id="cl-modal-title" className="cl-modal__title">
                    {selected.display_name}
                  </h2>
                </div>
                <button
                  type="button"
                  className="cl-modal__close"
                  onClick={closeRecord}
                  aria-label={t("cancel", "Cancel")}
                >
                  <X size={18} />
                </button>
              </div>

              <p className="cl-modal__help">
                {selected.is_adult === false
                  ? t(
                      "consentMinorFormHelp",
                      "Photograph each page of the form signed by the parent or guardian (or upload the scan), check it, then validate. This unlocks the selected uses for this player."
                    )
                  : t(
                      "consentAdultFormHelp",
                      "Public / social use needs a signed club form. Video and heart rate for adults normally come from their profile."
                    )}
              </p>

              <fieldset className="cl-field cl-purpose-picks">
                <legend>
                  {t("consentCoveredUses", "Uses covered by this form")}
                </legend>
                {PURPOSES.map(({ key, labelKey, fallback }) => {
                  const already = isGranted(selected, key);
                  return (
                    <label
                      key={key}
                      className={`cl-check${already ? " is-done" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={already || form.purposes.includes(key)}
                        disabled={already || busy}
                        onChange={() => togglePurpose(key)}
                      />
                      <span>{t(labelKey, fallback)}</span>
                      {already ? (
                        <em>{t("consentAlreadyGranted", "already granted")}</em>
                      ) : null}
                    </label>
                  );
                })}
              </fieldset>

              {selected.is_adult !== true ? (
                <label className="cl-field">
                  <span>{t("consentGuardian", "Guardian name")}</span>
                  <input
                    value={form.guardian_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, guardian_name: e.target.value }))
                    }
                    required
                    autoComplete="name"
                  />
                </label>
              ) : null}

              <div className="cl-field">
                <span>{t("consentSignedFile", "Signed form")}</span>
                <ConsentDocumentCapture
                  pages={form.pages}
                  disabled={busy}
                  onChange={(pages) => setForm((f) => ({ ...f, pages }))}
                />
              </div>

              <label className="cl-field">
                <span>
                  {t("consentDocumentRef", "Document reference")}{" "}
                  <em>({t("optional", "optional")})</em>
                </span>
                <input
                  value={form.document_ref}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, document_ref: e.target.value }))
                  }
                  placeholder={t(
                    "consentDocumentRefHint",
                    "Club file number / scan id"
                  )}
                />
              </label>

              {formNeedsEvidence ? (
                <label className="cl-validate">
                  <input
                    type="checkbox"
                    checked={form.verified}
                    disabled={busy}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, verified: e.target.checked }))
                    }
                  />
                  <span>
                    {selected.is_adult === true
                      ? t(
                          "consentValidateAdult",
                          "I checked that this form is signed by the player and covers the selected uses."
                        )
                      : t(
                          "consentValidateMinor",
                          "I checked that this form is signed by the parent or guardian named above and covers the selected uses."
                        )}
                  </span>
                </label>
              ) : null}

              {error ? (
                <p className="cl-error cl-error--modal" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="cl-modal__actions">
                <button
                  type="button"
                  className="cl-btn cl-btn--ghost"
                  onClick={closeRecord}
                  disabled={busy}
                >
                  {t("cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  className="cl-btn cl-btn--primary"
                  disabled={busy}
                >
                  {busy
                    ? t("saving", "Saving…")
                    : formNeedsEvidence
                      ? t("consentValidateSave", "Validate consent")
                      : t("consentSaveGrant", "Save consent")}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </AdminLayout>
  );
}
